import React from 'react';

import { Alert, Box, Button, Chip, Divider, Stack, Typography } from '@mui/material';

import type { CapacityRoomRate } from './utils';
import { getApplyAllButtonLabel, getGroupStatus, groupRoomRatesByRoomType } from './utils';

interface Props {
  appliedRoomRateIds: Array<string>;
  disabled: boolean;
  failedRoomRateIds: Array<string>;
  isApplying: boolean;
  onApplyDefaults: (roomRates: Array<CapacityRoomRate>) => void;
  roomRates: Array<CapacityRoomRate>;
}

export default function CapacityStep({
  appliedRoomRateIds,
  disabled,
  failedRoomRateIds,
  isApplying,
  onApplyDefaults,
  roomRates,
}: Props) {
  if (roomRates.length === 0) {
    return <Alert severity="warning">Apply room rates in the previous step before setting capacity.</Alert>;
  }

  const roomTypeGroups = groupRoomRatesByRoomType(roomRates);
  const unappliedRoomRates = roomRates.filter((roomRate) => !appliedRoomRateIds.includes(roomRate.roomRateId));
  const allApplied = unappliedRoomRates.length === 0;
  // Scoped to what's on screen: failedRoomRateIds can hold stale ids from a superseded rate-plan
  // pass, which would otherwise pin the failure banner even once every row here is truly applied.
  const currentRoomRateIds = new Set(roomRates.map((roomRate) => roomRate.roomRateId));
  const hasFailures = failedRoomRateIds.some((id) => currentRoomRateIds.has(id) && !appliedRoomRateIds.includes(id));

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        <Typography variant="body2">
          Deal options won&apos;t appear on the front end until their room rate has a capacity set. Capacity is stored
          per room rate, so defaulting a room type to 2A writes a 2-adult combination to every rate plan it sells.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          You will still be able to update the capacity anytime afterwards in{' '}
          <strong>Offer Builder → Rates and Inventory → room type → room rate → Capacity</strong>.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          We are only setting these defaults to ensure deal options appear on the front end.
        </Typography>
      </Alert>

      {allApplied && !hasFailures && (
        <Alert severity="success">Default capacity (2A) has been applied to every room type below.</Alert>
      )}

      {hasFailures && (
        <Alert severity="error">
          Couldn&apos;t apply default capacity to some room rates. Try again, or set these manually afterwards in Offer
          Builder → Rates and Inventory.
        </Alert>
      )}

      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ px: 2, py: 1.25, bgcolor: 'grey.50' }}
        >
          <Typography variant="subtitle2">Room types ({roomTypeGroups.length})</Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => onApplyDefaults(unappliedRoomRates)}
            disabled={disabled || isApplying || allApplied}
          >
            {getApplyAllButtonLabel(isApplying, unappliedRoomRates.length, roomRates.length)}
          </Button>
        </Stack>
        <Divider />
        {roomTypeGroups.map((group) => {
          const roomRateIds = group.roomRates.map((roomRate) => roomRate.roomRateId);
          const status = getGroupStatus(roomRateIds, appliedRoomRateIds, failedRoomRateIds);
          const unappliedGroupRoomRates = group.roomRates.filter(
            (roomRate) => !appliedRoomRateIds.includes(roomRate.roomRateId),
          );

          return (
            <Box key={group.roomTypeId}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1 }} gap={2}>
                <Stack sx={{ minWidth: 0 }}>
                  <Typography variant="body2">{group.roomName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {group.ratePlanNames.length > 0
                      ? group.ratePlanNames.join(', ')
                      : `${roomRateIds.length} rate plan${roomRateIds.length === 1 ? '' : 's'}`}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
                  {status === 'applied' && <Chip label="2A applied" size="small" color="success" variant="outlined" />}
                  {status === 'failed' && <Chip label="Failed" size="small" color="error" variant="outlined" />}
                  {status === 'partial' && (
                    <Chip label="Partially set" size="small" color="warning" variant="outlined" />
                  )}
                  {status === 'not-set' && <Chip label="Not set" size="small" variant="outlined" />}
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => onApplyDefaults(unappliedGroupRoomRates)}
                    disabled={disabled || isApplying || unappliedGroupRoomRates.length === 0}
                  >
                    Default to 2A
                  </Button>
                </Stack>
              </Stack>
              <Divider />
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
}
